import React, { useEffect } from 'react';

import { Box, Button, Link, Text } from '@opendoor/bricks-next';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { trackEvent } from '@opendoor/shared-fe/build/analytics';

import { getHuman_web_userSession_human } from 'src/api/queries/__generated__/getHuman';
import { ColumnContainer } from 'src/components/layouts/TwoColumnPageLayout';
import { buildAccountCreationAnswers } from 'src/helpers/answers';
import { Session } from 'src/helpers/session';
import { useGetHumanQuery } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getCurrentSlug, getInApp } from 'src/store/router/selectors';
import { handleException, removeHuman } from 'src/store/sellerFlow/actions';
import { leadToken } from 'src/store/sellerFlow/selectors';

import { submitChangedAnswers } from './AccountPage';
import { InContainer } from './CrossFadeAnimation';
import FlowPageHeader from './FlowPageHeader';

type AccountConfirmationProps = {
  human: getHuman_web_userSession_human & { email: string };
  title: string;
  subtitle?: string;
};

export const AccountConfirmation: React.FC<AccountConfirmationProps> = ({
  human,
  title,
  subtitle,
}) => {
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [signOutLoading, setSignOutLoading] = React.useState(false);
  const [showError, setshowError] = React.useState(false);

  const dispatch = useAppDispatch();
  const sellerInputUuid = useAppSelector(leadToken);
  const currentSlug = useAppSelector(getCurrentSlug);
  const inApp = useAppSelector(getInApp);

  const { refetch: refetchHuman } = useGetHumanQuery();

  const onContinue = () => {
    trackEvent('flow-slug', 'change', 'account-on-continue', {
      currentSlug,
      human,
      seller_input_uuid: sellerInputUuid,
    });

    setPasswordLoading(true); // mimic signing in

    const answersToSubmit = buildAccountCreationAnswers(
      human?.full_name ?? '',
      human?.email ?? '',
      human?.primary_phone ?? undefined,
    );
    submitChangedAnswers(dispatch, answersToSubmit, true);
    setPasswordLoading(false);
  };

  const onSignOut = () => {
    setSignOutLoading(true);
    Session.logOut()
      .then((_) => {
        setSignOutLoading(false);
        refetchHuman();
      })
      .catch((exception) => {
        dispatch(handleException(exception, 'sign-out', 'warn'));
        setSignOutLoading(false);
        dispatch(removeHuman());
        setshowError(true);
      });
  };

  useEffect(() => {
    if (showError) {
      showToast(
        'error',
        'Unable to sign out. Please try again.',
        'reception-fe-sign-out-error-toast',
        {
          toastOptions: { autoClose: 7000 },
        },
      );
      setshowError(false);
    }
  }, [showError]);

  return (
    <ColumnContainer>
      <Box flex={1}>
        <InContainer>
          <FlowPageHeader title={title} subtitle={subtitle} />
        </InContainer>
      </Box>

      <Box flex={1} width="100%" $largerThanMD={{ width: '70%' }}>
        <InContainer>
          <Text typography="$bodyMedium" tag="h1" marginTop={6} $smallerThanMD={{ marginTop: 0 }}>
            You are signed in as{' '}
            <Text numberOfLines={1} typography="$bodyMedium" fontWeight="900" tag="span">
              {human.email}
            </Text>
          </Text>

          <Box width="100%">
            <Button
              onPress={onContinue}
              loading={passwordLoading || signOutLoading}
              analyticsName="reception-fe-continue-signed-in"
              aria-label="Continue"
              label="Continue"
              variant="primary"
              size="small"
              borderRadius="$8x"
              height={53}
            />
            {!inApp && (
              <Link
                onPress={onSignOut}
                analyticsName="reception-fe-sign-out-switch-account"
                aria-label="Sign out and switch to a different account"
                size="medium"
                fontWeight="600"
              >
                Sign out and switch to a different account
              </Link>
            )}
          </Box>
        </InContainer>
      </Box>
    </ColumnContainer>
  );
};
